// OQueDizem.js — "O que dizem sobre este livro": a resenha da editora e os
// trechos que a própria obra traz (prefácio, orelha, apresentação).
//
// ================= POR QUE EXISTE (07/10/2026) =================
//
// Dos 214 livros da loja, 4 tinham alguma avaliação. A página de um livro sem
// nenhuma parece a de um livro que ninguém leu. O pedido original para isso
// foi "bots de avaliação" — notas inventadas por perfis que parecessem reais —,
// e foi recusado: é publicidade enganosa (art. 37 do CDC) e, para uma editora
// ACADÊMICA, o fim da credibilidade no dia em que um autor descobrisse que a
// resenha do livro dele foi escrita por um robô.
//
// Isto é o que o dono escolheu no lugar, e a diferença está no desenho:
//   - a resenha sai com o selo "Resenha editorial" e a assinatura da editora.
//     Ninguém a lê como opinião de leitor, porque ela não finge ser;
//   - cada trecho diz QUEM escreveu e DE ONDE veio ("Prefácio"), e é texto que
//     já está no livro;
//   - nada aqui tem estrela, e nada entra na média: a nota continua sendo só de
//     quem comprou (ver Opinioes.js).
//
// SEM CONTEÚDO, A SEÇÃO NÃO EXISTE — nem título, nem caixa vazia. Um "ainda não
// há resenha" seria o mesmo sinal de abandono que ela veio resolver.
import React, { useState } from "react";
import Reveal from "../../ui/Reveal";
import CabecalhoDeSecao from "./CabecalhoDeSecao";
import BotaoVerMais from "./BotaoVerMais";

// Acima disso a resenha recolhe com "Ler a resenha inteira": no celular, 3000
// caracteres são duas telas e meia antes do resto da página.
const LIMITE_RESENHA = 520;

const ASSINATURA_PADRAO = "Equipe editorial da Insigne Acadêmica";

export default function OQueDizem({ ebook }) {
  const [aberta, setAberta] = useState(false);

  const resenha = String(ebook?.resenha_editorial || "").trim();
  const assinatura = String(ebook?.resenha_editorial_assinatura || "").trim() || ASSINATURA_PADRAO;
  const citacoes = (Array.isArray(ebook?.citacoes) ? ebook.citacoes : []).filter(
    (c) => c && String(c.texto || "").trim()
  );

  if (!resenha && citacoes.length === 0) return null;

  const longa = resenha.length > LIMITE_RESENHA;

  return (
    <Reveal as="section" className="mb-12" aria-labelledby="o-que-dizem">
      <CabecalhoDeSecao chapeu="Sobre a obra" titulo="O que dizem sobre este livro" id="o-que-dizem" />

      {resenha && (
        <article className="cartao p-[1.125rem] sm:p-6">
          <span className="selo border-2 border-insigne-orange/50 bg-insigne-orange/10 text-insigne-orange">
            <i className="bi bi-patch-check-fill" aria-hidden="true" /> Resenha editorial
          </span>

          <div className="relative mt-[1rem]">
            <p
              className={
                "max-w-[72ch] whitespace-pre-line text-[0.975rem] leading-relaxed text-white/85 " +
                (longa && !aberta ? "max-h-[11.5rem] overflow-hidden" : "")
              }
            >
              {resenha}
            </p>
            {/* O degradê diz "tem mais" sem precisar de reticências no meio
                de uma frase. Some quando aberta. */}
            {longa && !aberta && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-insigne-navy-soft to-transparent"
              />
            )}
          </div>

          {longa && (
            <BotaoVerMais
              aberto={aberta}
              aoAlternar={() => setAberta((v) => !v)}
              textoFechado="Ler a resenha inteira"
              textoAberto="Mostrar menos"
            />
          )}

          <p className="mt-[1rem] text-sm font-semibold text-white">{assinatura}</p>
        </article>
      )}

      {citacoes.length > 0 && (
        <div
          className={
            "grid grid-cols-1 gap-[1rem] " +
            (resenha ? "mt-[1.25rem] " : "") +
            // Um trecho sozinho ocupa a linha inteira: meia largura com um
            // vazio ao lado parece um cartão que esqueceu o par.
            (citacoes.length > 1 ? "sm:grid-cols-2" : "")
          }
        >
          {citacoes.map((c, i) => (
            <figure key={i} className="cartao flex flex-col p-[1.125rem] sm:p-6">
              <i className="bi bi-quote text-3xl leading-none text-insigne-orange/70" aria-hidden="true" />
              <blockquote className="mt-2 flex-1 whitespace-pre-line text-[0.95rem] italic leading-relaxed text-white/85">
                {c.texto}
              </blockquote>
              {(c.autor || c.origem) && (
                <figcaption className="mt-[1rem] flex flex-wrap items-center gap-2 text-sm">
                  {c.autor && <span className="font-semibold text-white">{c.autor}</span>}
                  {c.origem && <span className="selo border-2 border-insigne-sombra bg-insigne-navy-deep text-white/60">{c.origem}</span>}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </Reveal>
  );
}
